import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Activity, LayoutDashboard, Grid2X2, BookOpen, Calendar, Settings, LogOut, Menu, X, UserPlus, QrCode, Trophy, Globe, Ticket, Radio, CreditCard, Wallet, Target } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useSportsAuth } from '../SportsAuthContext';

const NAV = [
  { to: '/sports/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/sports/courts', icon: Grid2X2, label: 'My Courts' },
  { to: '/sports/bookings', icon: BookOpen, label: 'Bookings' },
  { to: '/sports/live', icon: Radio, label: 'Live' },
  { to: '/sports/tournaments', icon: Trophy, label: 'Tournaments' },
  { to: '/sports/walk-in', icon: UserPlus, label: 'Walk-In' },
  { to: '/sports/money', icon: Wallet, label: 'Money' },
  { to: '/sports/vouchers', icon: Ticket, label: 'Vouchers' },
  // K6 P2: only when a rental kiosk phone is linked to this org (organization.has_kiosk from /me).
  { to: '/sports/ball-machine', icon: Target, label: 'Ball Machine', requires: 'has_kiosk' },
  { to: '/sports/scan', icon: QrCode, label: 'Scan' },
  { to: '/sports/monitoring', icon: Calendar, label: 'Calendar' },
  { to: '/sports/site-builder', icon: Globe, label: 'Site' },
  { to: '/sports/billing', icon: CreditCard, label: 'Billing' },
  { to: '/sports/settings', icon: Settings, label: 'Settings' },
];

const SIDEBAR_W = 224;

export default function SportsNavBar({ pendingCount = 0 }) {
  const { logout, organization } = useSportsAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Shift page content to the right on desktop so the fixed sidebar doesn't overlap.
  useEffect(() => {
    document.body.classList.add('sports-nav-shifted');
    return () => document.body.classList.remove('sports-nav-shifted');
  }, []);

  const scanPath = organization?.slug ? `/sports/scan/${organization.slug}` : '/sports/scan';
  const NAV_WITH_SCAN = NAV
    .filter(n => !n.requires || organization?.[n.requires])
    .map(n => n.to === '/sports/scan' ? { ...n, to: scanPath } : n);

  const handleLogout = () => { logout(); navigate('/sports/login'); };

  const renderNavItem = (n, { forSidebar = false, onNav } = {}) => {
    const active = pathname.startsWith(n.to);
    const isScan = n.label === 'Scan';
    const Icon = n.icon;

    const commonStyle = {
      color: active ? '#fff' : '#A3A3A3',
      background: active ? 'rgba(230,57,70,0.14)' : 'transparent',
      textDecoration: 'none',
      borderLeft: forSidebar ? `3px solid ${active ? '#E63946' : 'transparent'}` : 'none',
    };
    const className = forSidebar
      ? 'flex items-center gap-3 pl-4 pr-3 py-2.5 text-sm font-medium transition-all rounded-r-md hover:bg-white/5'
      : 'flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors';

    const inner = (
      <>
        <Icon className={forSidebar ? 'h-4 w-4 shrink-0' : 'h-4 w-4'} strokeWidth={active ? 2.4 : 2} />
        <span className="truncate">{n.label}</span>
        {isScan && <span style={{ fontSize: 9, opacity: 0.5, marginLeft: 2 }}>↗</span>}
        {n.label === 'Bookings' && pendingCount > 0 && (
          <span className="ml-auto h-5 min-w-5 rounded-full text-white text-xs flex items-center justify-center px-1.5"
            style={{ background: '#E63946', fontSize: 10, fontWeight: 700 }}>
            {pendingCount > 9 ? '9+' : pendingCount}
          </span>
        )}
      </>
    );

    if (isScan) {
      return (
        <a key={n.to} href={n.to} target="_blank" rel="noreferrer"
          data-testid={forSidebar ? 'sports-sidebar-nav-scan' : 'sports-nav-scan'}
          onClick={onNav}
          className={className}
          style={commonStyle}>
          {inner}
        </a>
      );
    }
    return (
      <Link key={n.to} to={n.to}
        data-testid={`${forSidebar ? 'sports-sidebar-nav-' : 'sports-nav-'}${n.label.toLowerCase().replace(' ', '-')}`}
        onClick={onNav}
        className={className}
        style={commonStyle}>
        {inner}
      </Link>
    );
  };

  return (
    <>
      {/* ── Desktop Sidebar (md+) ─────────────────────────────────────── */}
      <aside
        data-testid="sports-sidebar"
        className="hidden md:flex fixed left-0 top-0 bottom-0 flex-col z-30"
        style={{
          width: SIDEBAR_W,
          background: '#0A0A0A',
          borderRight: '1px solid rgba(255,255,255,0.08)',
        }}>
        {/* Brand */}
        <Link to="/sports/dashboard" className="flex items-center gap-2.5 px-5 shrink-0"
          style={{ height: 64, borderBottom: '1px solid rgba(255,255,255,0.06)', textDecoration: 'none' }}>
          <span className="h-9 w-9 rounded-md flex items-center justify-center shadow-lg"
            style={{ background: '#E63946', boxShadow: '0 0 14px rgba(230,57,70,0.45)' }}>
            <Activity className="h-5 w-5 text-white" strokeWidth={2.5} />
          </span>
          <span className="text-sm font-bold tracking-tight"
            style={{ fontFamily: 'Oswald, sans-serif', color: '#fff', letterSpacing: 0.4 }}>
            AGRIBOOKS <span style={{ color: '#E63946' }}>SPORTS</span>
          </span>
        </Link>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 pr-1 space-y-0.5">
          {NAV_WITH_SCAN.map(n => renderNavItem(n, { forSidebar: true }))}
        </nav>

        {/* Footer / Sign out */}
        <div className="shrink-0 px-3 py-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          {organization?.name && (
            <div className="px-2 pb-2.5 truncate" style={{ fontSize: 11, color: '#555', letterSpacing: 0.5 }}>
              <span style={{ textTransform: 'uppercase', fontWeight: 700 }}>Facility</span>
              <div className="truncate" style={{ color: '#A3A3A3', fontSize: 12, marginTop: 2 }}>{organization.name}</div>
            </div>
          )}
          <button onClick={handleLogout} data-testid="sports-sidebar-logout-btn"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors"
            style={{ color: '#A3A3A3', background: 'transparent' }}
            onMouseEnter={e => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#A3A3A3'; e.currentTarget.style.background = 'transparent'; }}>
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* ── Mobile Top Bar (below md) ─────────────────────────────────── */}
      <header className="md:hidden sticky top-0 z-30 bg-black/95 backdrop-blur-xl border-b"
        style={{ borderBottomColor: 'rgba(255,255,255,0.08)' }}>
        <div className="px-4 flex items-center justify-between" style={{ height: 56 }}>
          <Link to="/sports/dashboard" className="flex items-center gap-2 shrink-0" style={{ textDecoration: 'none' }}>
            <span className="h-8 w-8 rounded-md flex items-center justify-center shadow-lg"
              style={{ background: '#E63946', boxShadow: '0 0 14px rgba(230,57,70,0.45)' }}>
              <Activity className="h-4 w-4 text-white" strokeWidth={2.5} />
            </span>
            <span className="text-sm font-bold tracking-tight"
              style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>
              AGRIBOOKS <span style={{ color: '#E63946' }}>SPORTS</span>
            </span>
          </Link>
          <button className="p-1.5 rounded-md" style={{ color: '#A3A3A3' }}
            onClick={() => setMobileOpen(v => !v)} data-testid="sports-mobile-menu-btn">
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Drawer */}
        {mobileOpen && (
          <div className="border-t px-3 py-3 space-y-1"
            style={{ background: '#0A0A0A', borderColor: 'rgba(255,255,255,0.08)' }}>
            {NAV_WITH_SCAN.map(n => renderNavItem(n, { onNav: () => setMobileOpen(false) }))}
            <button onClick={handleLogout} data-testid="sports-mobile-logout-btn"
              className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm mt-2"
              style={{ color: '#A3A3A3', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 12, marginTop: 8 }}>
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        )}
      </header>
    </>
  );
}
